import { Dialog } from '@mui/material';
import {
  getDoc,
  doc,
  collection,
  getDocs,
  query,
  limit,
  orderBy,
} from 'firebase/firestore';
import NextError from 'next/error';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { mdiLock, mdiLockOff } from '@mdi/js';
import Icon from '@mdi/react';
import { db } from '@/firebase/firebase';
import eventAdd from '@/lib/eventAdd';
import { useAuth } from '@/providers/AuthProvider';
import PageLoading from '@/components/General/PageLoading';
import CollectionFollowButton from './CollectionFollowButton';
import CollectionStream from './CollectionStream';
import { collectionUrl, notNull } from '@/lib/utils';
import CollectionPrivate from './CollectionPrivate';
import CollectionAdminMakePrivateModal from './CollectionAdminMakePrivateModal';
import CollectionAdminMakePublicModal from './CollectionAdminMakePublicModal';
import PrivacyLock from './PrivacyLock';
import { ICollection } from '@/types/Collections/collections';
import { IPost } from '@/types/Collections/posts';
import PageModal from '@/components/General/PageModal';

const Collection = ({
  collection: userCollection,
  activities,
}: {
  collection: ICollection;
  activities: IPost[];
}): JSX.Element => {
  const { isLoggedIn, uid, isAdmin, authLoaded, auth } = useAuth();
  const [showAdminMakePrivateModal, setShowAdminMakePrivateModal] =
    useState(false);
  const [showAdminMakePublicModal, setShowAdminMakePublicModal] =
    useState(false);
  const [posts, setPosts] = useState(
    userCollection.isPrivate ? [] : activities,
  );
  const [arePostsLoading, setPostsLoading] = useState(
    !!userCollection.isPrivate,
  );

  useEffect(() => {
    const getActivities = async () => {
      if (userCollection.id && userCollection.author?.uid) {
        const ref = collection(
          db,
          'userCollections',
          userCollection.author.uid,
          'collections',
          userCollection.id,
          'posts',
        );
        const activities = await getDocs(
          query(ref, limit(100), orderBy('added', 'desc')),
        );
        if (activities && activities.docs?.length) {
          const promises = activities.docs.map(async activity =>
            getDoc(doc(db, 'posts', activity.id)).then(post => {
              if (post.exists()) {
                const postData = post.data();
                if (postData.isPublished && !postData.isDeleted) {
                  return { id: activity.id, ...activity.data(), ...postData };
                }
              }
              return null;
            }),
          );

          const posts = await Promise.allSettled(promises).then(posts =>
            posts
              .map(post => {
                if (post.status === 'fulfilled' && post.value) {
                  return post.value as IPost;
                }
                return null;
              })
              .filter(notNull),
          );
          setPosts(posts);
        }
      }
      setPostsLoading(false);
    };

    if (
      userCollection &&
      userCollection.isPrivate &&
      (uid === userCollection.author?.uid || isAdmin)
    ) {
      getActivities();
    } else if (activities) {
      setPosts(activities);
      setPostsLoading(false);
    }
  }, [userCollection, uid, activities, isAdmin]);

  useEffect(() => {
    if (userCollection?.id && uid && auth?.email) {
      eventAdd({
        uid,
        eventType: 'COLLECTION:VIEW',
        eventSource: 'COLLECTION',
        eventSourceId: userCollection.id,
        meta: {},
        email: auth.email,
        platform: 'web',
      });
    }
  }, [userCollection, uid, auth?.email]);

  const handleCloseAdminMakePrivateModal = () => {
    setShowAdminMakePrivateModal(false);
  };

  const handleCloseAdminMakePublicModal = () => {
    setShowAdminMakePublicModal(false);
  };

  if (!authLoaded) {
    return <PageLoading />;
  }

  if (!userCollection || userCollection.isDeleted) {
    return <NextError statusCode={404} />;
  }

  if (
    !isAdmin &&
    uid !== userCollection.author?.uid &&
    userCollection.isPrivate
  ) {
    return <CollectionPrivate />;
  }

  return (
    <div className="collection">
      <Head>
        <title>{userCollection.title}</title>
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}${collectionUrl(
            userCollection,
          )}`}
        />
        <meta property="og:type" content="website" />
      </Head>
      <header
        className={`collection-header hero${
          userCollection.colourScheme
            ? ` colour-${userCollection.colourScheme}`
            : ''
        }`}>
        <div className="hero-body">
          <div className="container is-narrow">
            {userCollection.image ? (
              <figure className="collection-image image is-16by9">
                <Image
                  src={userCollection.image}
                  alt={userCollection.title}
                  fill
                />
              </figure>
            ) : (
              ''
            )}
            <h1 className="title is-size-1 is-size-3-mobile">
              {userCollection.title}
            </h1>
            {(userCollection.isPrivate ||
              userCollection.author?.uid === uid) && (
              <PrivacyLock userCollection={userCollection} />
            )}
            {userCollection.description ? (
              <p className="curation-header-description is-font-brand-bold mb-4">
                {userCollection.description}
              </p>
            ) : (
              ''
            )}
            <div>
              <Link href={`/@${userCollection.author?.username}/collections`}>
                Curated by @{userCollection.author?.username}
              </Link>
            </div>
          </div>
        </div>
      </header>
      {userCollection.author?.uid !== uid && (
        <div className="container is-narrow mb-4">
          <CollectionFollowButton collection={userCollection} />
        </div>
      )}
      <div className="buttons is-centered">
        {isLoggedIn && userCollection.author?.uid === uid && (
          <Link
            legacyBehavior
            href={`/collections/edit?id=${userCollection.id}`}
            as={`/collections/edit/${userCollection.id}`}>
            <a className="button">
              <span>Edit collection</span>
            </a>
          </Link>
        )}
        {isAdmin &&
          userCollection.author?.uid !== uid &&
          !userCollection.isPrivate && (
            <button
              type="button"
              onClick={() => {
                setShowAdminMakePrivateModal(true);
              }}
              className="button is-danger">
              <span className="icon">
                <Icon path={mdiLock} size={1} />
              </span>
              <span>Make private</span>
            </button>
          )}
        {isAdmin &&
          userCollection.author?.uid !== uid &&
          userCollection.isPrivate &&
          userCollection.adminLockedPrivate && (
            <button
              type="button"
              onClick={() => {
                setShowAdminMakePublicModal(true);
              }}
              className="button is-danger">
              <span className="icon">
                <Icon path={mdiLockOff} size={1} />
              </span>
              <span>Make public</span>
            </button>
          )}
      </div>
      <CollectionStream
        posts={posts}
        collection={userCollection}
        arePostsLoading={arePostsLoading}
      />
      {isAdmin && (
        <>
          <Dialog
            open={showAdminMakePrivateModal}
            onClose={handleCloseAdminMakePrivateModal}
            TransitionProps={{
              onExited: handleCloseAdminMakePrivateModal,
            }}
            aria-labelledby="collection-options-title">
            <CollectionAdminMakePrivateModal
              handleClose={handleCloseAdminMakePrivateModal}
              collection={userCollection}
            />
          </Dialog>
          <Dialog
            open={showAdminMakePublicModal}
            onClose={handleCloseAdminMakePublicModal}
            TransitionProps={{
              onExited: handleCloseAdminMakePublicModal,
            }}
            aria-labelledby="collection-options-title">
            <CollectionAdminMakePublicModal
              handleClose={handleCloseAdminMakePublicModal}
              collection={userCollection}
            />
          </Dialog>
        </>
      )}
      {userCollection && <PageModal parent={collectionUrl(userCollection)} />}
    </div>
  );
};

export default Collection;
